
import React, { PureComponent } from 'react';

import logger from '../../../app/logger';
import { setCustomParticipantVisible, setObjectFit } from '../../../base/conference';
import { openDialog } from '../../../base/dialog';
import { translate } from '../../../base/i18n';
import {
    Icon,
    IconContain,
    IconCover,
    IconMessage,
    IconModeratorPin,
    IconUserPinned,
    IconVolume
} from '../../../base/icons';
import {
    SET_MODERATOR_PIN,
    isLocalParticipantModerator,
    pinParticipant
} from '../../../base/participants';
import { connect } from '../../../base/redux';
import { openChat } from '../../../chat/';
import { setIsPollsTabFocused } from '../../../chat/actions.any';
import {
    type Props as AbstractProps,
    _mapStateToProps as _abstractMapStateToProps
} from '../../../chat/components/PrivateMessageButton';
import { pollsTabFocusedHandler } from '../../../conference/actions.web';
import { NOTIFICATION_TYPE, showNotification } from '../../../notifications';
import {
    close,
    closeParticipantsPaneModal
} from '../../../participants-pane/actions';
import { ColoredIcon } from '../../../participants-pane/components/styled';
import { isButtonEnabled } from '../../../toolbox/functions.web';
import { LAYOUTS } from '../../../video-layout';

import VideoMenuButton from './VideoMenuButton';
import VolumeControlDialog from './VolumeControlDialog';

declare var interfaceConfig: Object;
const visibleButton = new Set(interfaceConfig.TOOLBAR_BUTTONS);


type Props = AbstractProps & {

    /**
     * The current conference.
     */
    _conference: Object,

    /**
     * True if the private chat functionality is disabled, hence the button is not visible.
     */
    _hidden: boolean,

    /**
     * True if the local participant is a moderator.
     */
    _isPersonModerator: boolean,

    /**
     * True if private chat is disabled by a moderator.
     */
    _isPrivateChatDisabled: boolean,

    /**
     * Current voice mode state for target participant.
     */
    _voiceModeState: Object,

    /**
     * Clone capability state for target participant.
     */
    _voiceCapability: Object
};

/**
 * A custom implementation of the PrivateMessageButton specialized for
 * the web version of the remote video menu. When the web platform starts to use
 * the {@code AbstractButton} component for the remote video menu, we can get rid
 * of this component and use the generic button in the chat feature.
 */
class PrivateMessageMenuButton extends PureComponent<Props> {
    /**
     * Instantiates a new Component instance.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);

        this._onClick = this._onClick.bind(this);
        this._onHandleObjectFit = this._onHandleObjectFit.bind(this);
        this._onHandlevolumeControlDialog = this._onHandlevolumeControlDialog.bind(this);
        this._onHandlePinParticipants = this._onHandlePinParticipants.bind(this);
        this._onHandlePinParticipantsByModerator = this._onHandlePinParticipantsByModerator.bind(this);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {

        const {
            participantID, _visibleButtons, t, _hidden, _participant, _isPersonModerator, objectfit,
            _filmstripview, _isPrivateChatDisabled, visible, _voiceModeState, _voiceCapability
        } = this.props;

        if (_hidden) {
            return null;
        }

        const isPinned = _participant && _participant.pinned;
        const isModeratorPinned = _participant && _participant.moderatorPin;
        const isFakeParticipant = _participant && _participant.isFakeParticipant;
        const cloneStatus = _voiceCapability?.cloned_voice_status || _voiceModeState?.cloned_voice_status || 'pending';
        const cloneStatusLabel = cloneStatus === 'ready'
            ? 'ready'
            : cloneStatus === 'processing' ? 'processing' : 'fallback to default voice';


        return (
            <div className = 'remotevideomenu-child'>
                {_filmstripview === LAYOUTS.TILE_VIEW && _visibleButtons.has('fit-video') && <li className = 'popupmenu__item private_msg_box'>
                    <button
                        className = 'popupmenu__link'
                        onClick = { this._onHandleObjectFit }>
                        <span className = 'popupmenu__icon'>
                            <ColoredIcon color = '#fff'>
                                <Icon
                                    fillColor = '#fff'
                                    src = { objectfit === 'cover' ? IconCover : IconContain } />
                            </ColoredIcon>
                        </span>
                        <span className = 'popupmenu__text'>
                            {objectfit === 'contain' ? t('tooltip.switchToContain') : t('tooltip.switchToCover')}
                        </span>
                    </button>
                </li>}

                {_isPersonModerator && _visibleButtons.has('moderator-pin') && _filmstripview !== LAYOUTS.TILE_VIEW && (
                    <li className = 'popupmenu__item private_msg_box pin_private_button'>
                        <button
                            className = 'popupmenu__link'
                            onClick = { this._onHandlePinParticipantsByModerator }>
                            <span className = 'popupmenu__icon'>
                                <ColoredIcon color = '#fff'>
                                    <Icon src = { IconModeratorPin } />
                                </ColoredIcon>
                            </span>
                            <span className = 'popupmenu__text'>
                                {isModeratorPinned
                                    ? t('videothumbnail.unpinEveryone') : t('videothumbnail.pinEveryone')
                                }
                            </span>
                        </button>
                    </li>
                )}

                { _filmstripview !== LAYOUTS.TILE_VIEW && _visibleButtons.has('pin-user') && <li className = 'popupmenu__item private_msg_box'>
                    <button
                        className = 'popupmenu__link'
                        onClick = { this._onHandlePinParticipants }>
                        <span className = 'popupmenu__icon pin_private_button'>
                            <ColoredIcon color = '#fff'>
                                <Icon
                                    fillColor = '#fff'
                                    src = { IconUserPinned } />
                            </ColoredIcon>
                        </span>
                        <span className = 'popupmenu__text'>
                            {isPinned ? t('videothumbnail.unpinMe') : t('videothumbnail.pinMe')}
                        </span>
                    </button>
                </li>}

                {_visibleButtons.has('chat') && !isFakeParticipant && !_isPrivateChatDisabled && visible && <VideoMenuButton
                    buttonText = { t('toolbar.privateMessage') }
                    icon = { IconMessage }
                    id = { `privmsglink_${participantID}` }
                    onClick = { this._onClick }
                    privatemessage = { true } />}
                {_visibleButtons.has('volume-control') && !isFakeParticipant && <li className = 'popupmenu__item private_msg_box'>
                    <button
                        className = 'popupmenu__link'
                        onClick = { this._onHandlevolumeControlDialog }>
                        <span className = 'popupmenu__icon'>
                            <ColoredIcon color = '#fff'>
                                <Icon src = { IconVolume } />
                            </ColoredIcon>
                        </span>
                        <span className = 'popupmenu__text'>
                            {t('volumeSlider.label')}
                        </span>
                    </button>
                </li>}

                {!isFakeParticipant && <li className = 'popupmenu__item private_msg_box'>
                    <button
                        className = 'popupmenu__link'
                        disabled = { true }>
                        <span className = 'popupmenu__icon'>
                            <ColoredIcon color = '#fff'>
                                <Icon src = { IconVolume } />
                            </ColoredIcon>
                        </span>
                        <span className = 'popupmenu__text'>
                            {`Clone status: ${cloneStatusLabel}`}
                        </span>
                    </button>
                </li>}
            </div>
        );
    }


    /**
     * Callback to be invoked on pressing the button.
     *
     * @returns {void}
     */
    _onClick() {
        const { dispatch, _participant } = this.props;

        dispatch(openChat(_participant));
        dispatch(closeParticipantsPaneModal());
        dispatch(close());
        dispatch(pollsTabFocusedHandler(false));
        dispatch(setIsPollsTabFocused(false));
    }


    /**
     * callback to be invoked on pressing the object fit button
     */
    _onHandleObjectFit() {
        const { dispatch } = this.props;

        dispatch(
            setObjectFit(
                this.props.objectfit === 'cover' ? 'contain' : 'cover'
            )
        );
    }


    /**
     * Callback to be invoked on pressing the pin participants button.
     *
     * @returns {void}
     */
    _onHandlePinParticipants(e) {
        const { dispatch, participantID } = this.props;

        e.stopPropagation();
        e.preventDefault();

        dispatch(pinParticipant(participantID));
        dispatch(setCustomParticipantVisible(false));
    }

    /**
     * Opens the volume control dialog for the participant.
     * @returns {void}
     */
    _onHandlevolumeControlDialog() {
        const { dispatch, participantID } = this.props;

        dispatch(openDialog(VolumeControlDialog, { participantID }));
    }


    /**
     * Callback to be invoked on pressing the moderator pin button.
     *
     * @returns {void}
     */
    _onHandlePinParticipantsByModerator() {
        const { _conference: conference, dispatch, participantID, _participant, t } = this.props;

        if (!conference) {
            logger.error('Conference is not available');

            return;
        }

        const myId = conference.myUserId();

        // Determine action based on current state
        const isModeratorPinned = _participant && _participant.moderatorPin;
        const pinAction = isModeratorPinned ? 'unpin' : 'pin';

        // Send message to other participants
        conference.sendMessage(
            {
                type: SET_MODERATOR_PIN,
                participant_id: participantID,
                senderId: myId,
                action: pinAction
            }
        );

        // Show notification
        const notificationKey = pinAction === 'pin'
            ? t('videothumbnail.pinByModerator')
            : t('videothumbnail.unpinByModerator');
        const notificationDescriptionKey = pinAction === 'pin'
            ? t('videothumbnail.moderatorPinDiscription')
            : t('videothumbnail.unpinByModerator');

        dispatch(
        showNotification(
            {
                appearance: NOTIFICATION_TYPE.NORMAL,
                titleKey: notificationKey,
                descriptionKey: notificationDescriptionKey
            },
            3000
        )
        );
    }

}

/**
 * Maps part of the Redux store to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @param {Props} ownProps - The own props of the component.
 * @returns {Props}
 */
function _mapStateToProps(state: Object, ownProps: Props): $Shape<Props> {
    const objectfit = state['features/base/conference'].objectFit;
    const _voiceModeState = state['features/subtitles']?._voiceModeByParticipant?.get?.(ownProps.participantID);
    const _voiceCapability = state['features/subtitles']?._voiceCapabilities?.get?.(ownProps.participantID);


    return {
        ..._abstractMapStateToProps(state, ownProps),
        _conference: state['features/base/conference'].conference,
        _hidden:
            typeof interfaceConfig !== 'undefined'
            && (interfaceConfig.DISABLE_PRIVATE_MESSAGES
                || !isButtonEnabled('chat', state)),
        _isPersonModerator: isLocalParticipantModerator(state),
        _visibleButtons: visibleButton,
        objectfit,
        _isPrivateChatDisabled: state['features/chat']?.isPrivateChatDisabled || false,
        _voiceModeState,
        _voiceCapability
    };
}

export default translate(connect(_mapStateToProps)(PrivateMessageMenuButton));
